import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { buildGraph, webPageJsonLd, breadcrumbJsonLd } from "@/data/seo";
import {
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  TrendingDown,
  Banknote,
  Clock,
  CheckCircle,
  Loader2,
  Home,
  Wallet,
  Percent,
  Target,
  User,
  Mail,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RED = "#D41F30";

const fmtMoney = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const REASONS = [
  { value: "Lower my monthly payment", emoji: "💸", desc: "Reduce my monthly cost" },
  { value: "Cash out equity", emoji: "💰", desc: "Tap into home equity" },
  { value: "Shorter loan term", emoji: "⏱️", desc: "Pay off faster" },
  { value: "Remove mortgage insurance (PMI)", emoji: "🛡️", desc: "Eliminate PMI" },
  { value: "Switch from ARM to fixed", emoji: "🔒", desc: "Lock in a fixed rate" },
  { value: "Consolidate debt", emoji: "📋", desc: "Roll high-interest debt in" },
];

const fmtPct = (n) => `${n.toFixed(2)}%`;

const RangeSlider = ({ min, max, step, value, onChange, testid }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="cw-slider w-full h-2 rounded-full appearance-none cursor-pointer"
      style={{
        background: `linear-gradient(to right, ${RED} 0%, ${RED} ${pct}%, #FCE7E8 ${pct}%, #FCE7E8 100%)`,
      }}
    />
  );
};

const SliderBlock = ({ label, valueDisplay, min, max, step, value, onChange, minLabel, maxLabel, testid }) => (
  <div>
    <div className="flex items-end justify-between mb-4">
      <span className="text-base md:text-lg font-semibold text-gray-900">{label}</span>
      <span className="text-3xl md:text-4xl font-black tabular-nums" style={{ color: RED }}>
        {valueDisplay}
      </span>
    </div>
    <RangeSlider min={min} max={max} step={step} value={value} onChange={onChange} testid={testid} />
    <div className="flex justify-between text-sm text-gray-400 mt-3">
      <span>{minLabel}</span>
      <span>{maxLabel}</span>
    </div>
  </div>
);

const StepHeader = ({ stepNumber, Icon, title, subtitle }) => (
  <div className="mb-6 md:mb-10">
    <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4 md:mb-6">
      <Icon className="w-5 h-5 md:w-7 md:h-7" style={{ color: RED }} />
    </div>
    <div className="text-xs font-bold tracking-[0.18em] mb-2 md:mb-3" style={{ color: RED }}>
      STEP {stepNumber}
    </div>
    <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.05] tracking-tight mb-3 md:mb-4">
      {title}
    </h2>
    {subtitle && (
      <p className="text-sm md:text-lg text-gray-500 leading-relaxed max-w-2xl">
        {subtitle}
      </p>
    )}
  </div>
);

const PlainInput = ({ label, ...props }) => (
  <div>
    {label && <label className="block text-base font-semibold text-gray-900 mb-2">{label}</label>}
    <input
      {...props}
      className="w-full rounded-xl border-2 border-gray-200 bg-white px-5 py-4 text-lg text-gray-900 placeholder-gray-300 focus:border-[#D41F30] focus:outline-none transition-colors"
    />
  </div>
);

const Chip = ({ active, onClick, label, emoji, desc, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-4 rounded-2xl border-2 px-5 py-4 transition-all duration-200 hover:shadow-md text-left ${
      active ? "border-[#D41F30] bg-red-50/60 shadow-md" : "border-gray-200 bg-white hover:border-gray-300"
    }`}
  >
    <span className="text-2xl">{emoji}</span>
    <div className="flex-1">
      <div className="text-base font-semibold text-gray-900">{label}</div>
      {desc && <div className="text-xs text-gray-500 mt-0.5">{desc}</div>}
    </div>
    {active && (
      <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: RED }}>
        <CheckCircle className="w-4 h-4 text-white" />
      </div>
    )}
  </button>
);

const TOTAL_STEPS = 7;

function RefinanceForm() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    reasons: [],
    homeValue: 450000,
    currentBalance: 320000,
    currentRate: 7.0,
    creditScore: 700,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    consent: false,
  });

  const update = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const toggleReason = (v) =>
    setData((p) => ({
      ...p,
      reasons: p.reasons.includes(v) ? p.reasons.filter((x) => x !== v) : [...p.reasons, v],
    }));

  const canContinue = () => {
    switch (step) {
      case 1: return data.reasons.length > 0;
      case 2: return data.homeValue > 0;
      case 3: return data.currentBalance > 0;
      case 4: return data.currentRate >= 0;
      case 5: return data.creditScore >= 500;
      case 6: return data.firstName.trim() && data.lastName.trim();
      case 7:
        return (
          /^\S+@\S+\.\S+$/.test(data.email.trim()) &&
          data.phone.replace(/\D/g, "").length >= 10 &&
          data.consent
        );
      default: return false;
    }
  };

  const next = () => {
    setError(null);
    if (step < TOTAL_STEPS) setStep(step + 1);
    else submit();
  };
  const back = () => {
    setError(null);
    if (step > 1) setStep(step - 1);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/leads`, {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        timeline: "",
        location: "",
        leadType: "Refinance",
        // Reuse extended fields with refinance semantics
        targetPrice: data.homeValue, // home value
        annualIncome: null,
        downPayment: data.currentBalance, // current loan balance
        creditScore: data.creditScore,
        monthlyDebt: null,
        holdingBack: data.reasons,
        determination: Math.round(data.currentRate * 100), // current rate * 100 to preserve precision
        consent: data.consent,
      });
      if (res.data && res.data.syncStatus) {
        window.location.assign("/refinance-thanks");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const pct = (step / TOTAL_STEPS) * 100;

  return (
    <div className="w-full" data-testid="refinance-form">
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center gap-6">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: RED }} />
          </div>
          <div className="text-sm font-medium text-gray-500 tabular-nums">
            {step} of {TOTAL_STEPS}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-10 py-6 md:py-16">
        {step === 1 && (
          <div>
            <StepHeader
              stepNumber={1}
              Icon={Target}
              title="Why are you refinancing?"
              subtitle="Pick the goals that fit. We'll tailor your refi plan around them."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {REASONS.map((r) => (
                <Chip
                  key={r.value}
                  active={data.reasons.includes(r.value)}
                  onClick={() => toggleReason(r.value)}
                  label={r.value}
                  emoji={r.emoji}
                  desc={r.desc}
                  testid={`refi-reason-${r.value.split(' ')[0].toLowerCase()}`}
                />
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <StepHeader stepNumber={2} Icon={Home} title="What's your home worth today?" subtitle="A rough estimate is fine. We'll confirm later." />
            <SliderBlock
              label="Estimated home value"
              valueDisplay={`${fmtMoney(data.homeValue)}${data.homeValue >= 2000000 ? "+" : ""}`}
              min={50000} max={2000000} step={5000}
              value={data.homeValue} onChange={(v) => update("homeValue", v)}
              minLabel="$50,000" maxLabel="$2,000,000+"
              testid="refi-home-value"
            />
          </div>
        )}

        {step === 3 && (
          <div>
            <StepHeader stepNumber={3} Icon={Wallet} title="What's your current loan balance?" subtitle="Check your last mortgage statement for the most accurate number." />
            <SliderBlock
              label="Remaining loan balance"
              valueDisplay={`${fmtMoney(data.currentBalance)}${data.currentBalance >= 1500000 ? "+" : ""}`}
              min={10000} max={1500000} step={5000}
              value={data.currentBalance} onChange={(v) => update("currentBalance", v)}
              minLabel="$10,000" maxLabel="$1,500,000+"
              testid="refi-balance"
            />
          </div>
        )}

        {step === 4 && (
          <div>
            <StepHeader stepNumber={4} Icon={Percent} title="What's your current interest rate?" subtitle="If you're not sure, take your best guess. We'll verify." />
            <SliderBlock
              label="Current rate"
              valueDisplay={fmtPct(data.currentRate)}
              min={2} max={12} step={0.05}
              value={data.currentRate} onChange={(v) => update("currentRate", v)}
              minLabel="2%" maxLabel="12%"
              testid="refi-rate"
            />
          </div>
        )}

        {step === 5 && (
          <div>
            <StepHeader stepNumber={5} Icon={CheckCircle} title="What's your approximate credit score?" subtitle="Don't worry if it's not perfect. We work with scores as low as 580." />
            <SliderBlock
              label="Credit score"
              valueDisplay={data.creditScore}
              min={500} max={850} step={1}
              value={data.creditScore} onChange={(v) => update("creditScore", v)}
              minLabel="500" maxLabel="850"
              testid="refi-credit"
            />
          </div>
        )}

        {step === 6 && (
          <div>
            <StepHeader stepNumber={6} Icon={User} title="What's your name?" subtitle="So we know how to introduce ourselves." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-xl">
              <PlainInput label="First name" type="text" placeholder="Jane" value={data.firstName} onChange={(e) => update("firstName", e.target.value)} data-testid="refi-firstName" autoFocus />
              <PlainInput label="Last name" type="text" placeholder="Doe" value={data.lastName} onChange={(e) => update("lastName", e.target.value)} data-testid="refi-lastName" />
            </div>
          </div>
        )}

        {step === 7 && (
          <div>
            <StepHeader stepNumber={7} Icon={Mail} title={`How do we reach you, ${data.firstName || "friend"}?`} subtitle="We'll build your personalized refinance plan and have our team reach out." />
            <div className="space-y-5 max-w-xl">
              <PlainInput label="Email address" type="email" placeholder="jane@example.com" value={data.email} onChange={(e) => update("email", e.target.value)} data-testid="refi-email" autoFocus />
              <PlainInput label="Phone number" type="tel" placeholder="(555) 555-1234" value={data.phone} onChange={(e) => update("phone", e.target.value)} data-testid="refi-phone" />
              <label className="flex items-start gap-3 mt-6 cursor-pointer select-none">
                <input type="checkbox" checked={data.consent} onChange={(e) => update("consent", e.target.checked)} data-testid="refi-consent" className="mt-1 w-5 h-5 accent-[#D41F30] flex-shrink-0" />
                <span className="text-sm text-gray-600 leading-relaxed">
                  I agree to receive a phone call, email and SMS messages from CityWorth Mortgage and CityWorth Homes regarding my refinance plan. I understand consent isn&apos;t required to purchase.
                </span>
              </label>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-8 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between mt-8 md:mt-12">
          {step > 1 ? (
            <button
              type="button"
              onClick={back}
              disabled={submitting}
              className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium text-base px-2 py-2 disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={next}
            disabled={!canContinue() || submitting}
            className="inline-flex items-center gap-2 text-white font-semibold text-base rounded-xl px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: RED }}
            data-testid="refi-next"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : step === TOTAL_STEPS ? (
              <>
                Get my refinance plan
                <ArrowRight className="w-5 h-5" />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RefinancePage() {
  return (
    <div className="bg-white" data-testid="refinance-page">
      <SEO
        title="Refinance | Lower your rate or cash out equity"
        description="Refinance your home with CityWorth. Lower your monthly payment, cash out equity, switch ARM to fixed, remove PMI, or consolidate debt. Free refinance plan in minutes."
        path="/refinance"
        jsonLd={buildGraph(
          webPageJsonLd({
            name: "Refinance with CityWorth Mortgage",
            description: "Lower rate, cash-out, shorter term, and PMI-removal refinances.",
            path: "/refinance",
          }),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Refinance", path: "/refinance" },
          ])
        )}
      />
      {/* Hero */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-50 rounded-full blur-3xl opacity-50 -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-red-50 rounded-full px-4 py-2 mb-6">
                <RefreshCw className="w-4 h-4" style={{ color: RED }} />
                <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>
                  Refinance
                </span>
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.02] tracking-tight mb-6">
                Make your mortgage <span style={{ color: RED }}>work harder.</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-2xl">
                Lower your rate, tap into equity, or pay off your home faster. Tell us your goal and our team builds you a refinance plan that actually saves you money.
              </p>
              <div className="grid grid-cols-3 gap-3 max-w-lg">
                <div className="text-center bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                  <TrendingDown className="w-5 h-5 mx-auto mb-2" style={{ color: RED }} />
                  <p className="text-xs font-semibold text-gray-900">Lower rate</p>
                </div>
                <div className="text-center bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                  <Banknote className="w-5 h-5 mx-auto mb-2" style={{ color: RED }} />
                  <p className="text-xs font-semibold text-gray-900">Cash out</p>
                </div>
                <div className="text-center bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                  <Clock className="w-5 h-5 mx-auto mb-2" style={{ color: RED }} />
                  <p className="text-xs font-semibold text-gray-900">Shorter term</p>
                </div>
              </div>
              <div className="mt-8">
                <Button
                  className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  onClick={() => document.getElementById("refi-form").scrollIntoView({ behavior: "smooth" })}
                  data-testid="refi-hero-cta"
                >
                  Start my refinance plan
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=1200&q=80"
                  alt="Homeowner saving with refinance"
                  className="w-full h-[440px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="refi-form" className="bg-gray-50 border-t border-gray-100">
        <RefinanceForm />
      </section>
    </div>
  );
}
